import { CrudFilters, DataProvider } from "@refinedev/core";
import restDataProvider from "@refinedev/simple-rest";
import { stringify } from "query-string";
import { AxiosInstance } from "axios";
import CONFIG from "app-config/config.json";
import { axiosInstance } from "providerrs/axiosInstance";
import { DEFAULT_PAGE_SIZE } from "utills/constants";
import { isEmpty } from "lodash";

const API_URL = CONFIG.API;

export const formattedFilterParamsString = (filters: CrudFilters) => {
  return filters.map((params, index) =>
    Object.entries(params).map(
      (keyValue) => `filters[${index}][${keyValue[0]}]=${keyValue[1]}`.split(",")
    ).join("&")
  ).join("&");
}

const generateFilter = (filters?: CrudFilters) => {
  const queryFilters: { [key: string]: string } = {};
  if (filters) {
    filters.forEach((filter: any) => {
      if (
        filter.operator !== "or" &&
        filter.operator !== "and" &&
        "field" in filter
      ) {
        const { field, value } = filter;
        queryFilters[`${field}`] = value;
      }
    });
  }

  return queryFilters;
};


const provider = (axios: AxiosInstance): DataProvider => {
  return {
    ...restDataProvider(API_URL, axios as any),
    getList: async ({ resource, pagination, filters, meta }) => {
      const url = `${API_URL}/${resource}`;

      // pagination
      const pageSize = pagination?.pageSize || DEFAULT_PAGE_SIZE;

      const queryFilters = !isEmpty(filters) ? generateFilter(filters) : !isEmpty(meta?.filters) ? generateFilter(meta?.filters) : {};

      const query: {
        limit: number;
      } = {
        limit: pageSize,
      };

      const { data } = (meta?.offset) ? await axios.get(
        `${url}?${stringify(query)}&${stringify(queryFilters)}`
      ) : await axios.get(
        `${url}/page/${queryFilters.page || 1}?${stringify(query)}&${stringify(queryFilters)}`
      );

      return {
        data: data.data[meta?.resource ?? resource],
        total: data.data["paging"]?.total_page || 0,
        paging: data.data["paging"],
        pageSize: pageSize,
        ...data.data
      };
    },
    getOne: async ({ resource, id, meta }) => {
      const url = `${API_URL}/${resource}${id ? `/${id}` : ''}`;

      const queryFilters = meta?.filters ? generateFilter(meta.filters) : "";


      const { data } = await axios.get(`${url}${queryFilters ? "?" + stringify(queryFilters) : ""}`);

      return {
        data: data[meta?.resource || resource],
      };
    },
    create: async ({ resource, variables, meta }) => {
      const url = meta?.URLSuffix
        ? `${API_URL}/${resource}/${meta.URLSuffix}`
        : `${API_URL}/${resource}`;

      const { headers } = meta ?? {};

      const ignoreResourceWrapper = meta?.ignoreResourceWrapper ?? false;
      const newVariables = ignoreResourceWrapper ? variables : {
        [meta?.resource || resource]: variables,
      };

      const { data } = await axios.post(url, newVariables, {
        headers,
      });

      return {
        data,
      };
    },
    update: async ({ resource, id, variables, meta }) => {
      const url = meta?.URLSuffix
        ? `${API_URL}/${resource}${id ? `/${id}` : ''}/${meta.URLSuffix}`
        : `${API_URL}/${resource}${id ? `/${id}` : ''}`;

      const ignoreResourceWrapper = meta?.ignoreResourceWrapper ?? false;
      const newVariables = ignoreResourceWrapper ? variables : {
        [meta?.resource || resource]: variables,
      };
      const noBody = meta?.noBody ?? false;

      const { data } =
        meta?.method === "patch"
          ? noBody
            ? await axios.patch(url)
            : await axios.patch(url, newVariables)
          : noBody
            ? await axios.put(url)
            : await axios.put(url, newVariables);

      return {
        data,
      };
    },

    deleteOne: async ({ resource, id, variables, meta }) => {
      const url = meta?.URLSuffix
        ? `${API_URL}/${resource}/${id}/${meta.URLSuffix}`
        : `${API_URL}/${resource}/${id}`;

      const ignoreResourceWrapper = meta?.ignoreResourceWrapper ?? false;

      const newVariables = ignoreResourceWrapper ? variables : {
        [meta?.resource || resource]: variables,
      };
  
      const { data } = await axios.delete(url, {
        data: newVariables,
      });

      return {
        data,
      };
    },
  };
};

export const dataProvider = provider(axiosInstance);
